import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Eye, Edit, AlertCircle, CheckCircle, Lock, User, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_INFO = {
  admin: { label: 'Administrador', icon: Edit, color: 'bg-blue-500 text-white', description: 'Podrás crear y editar información en el sistema' },
  editor: { label: 'Editor', icon: Edit, color: 'bg-green-500 text-white', description: 'Podrás crear y editar información en fraccionamientos asignados' },
  viewer: { label: 'Visualizador', icon: Eye, color: 'bg-zinc-600 text-white', description: 'Podrás ver toda la información pero no modificarla' },
  comisionista: { label: 'Comisionista', icon: UserPlus, color: 'bg-[#C6A54A] text-black', description: 'Podrás ver lotes y crear solicitudes de venta' }
};

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [accepting, setAccepting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchInvitation();
  }, [token]);

  const fetchInvitation = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/invitation/${token}`);
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        setError('Error en la respuesta del servidor');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        setError(data.detail || 'Invitación no válida');
        return;
      }

      setInvitationData(data);
    } catch (err) {
      setError('Error al cargar la invitación');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Ingresa tu nombre');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setAccepting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token,
          name: formData.name,
          password: formData.password
        })
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Error en la respuesta del servidor');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Error al aceptar invitación');
      }

      toast.success('¡Bienvenido al equipo!');
      // Redirect based on role
      const role = invitation?.role;
      navigate(role === 'comisionista' ? '/lotes' : '/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#C6A54A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Invitación no válida</h2>
            <p className="text-zinc-400 mb-6">{error}</p>
            <Button
              onClick={() => navigate('/login')}
              className="bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-semibold"
            >
              Ir a Iniciar Sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const invitation = invitationData?.invitation;
  const account = invitationData?.account;
  const roleInfo = ROLE_INFO[invitation?.role] || ROLE_INFO.viewer;
  const RoleIcon = roleInfo.icon;

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_lotesys/artifacts/fyrjj46v_Logo%20IBA%C3%91EZ%20para%20desarrollos%20inmobiliarios.png" 
            alt="Ibañez Desarrollos"
            className="w-48 mx-auto mb-4"
          />
          <p className="text-zinc-400 text-sm">Sistema de Administración</p>
        </div>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="text-center border-b border-zinc-800 pb-6">
            <div className="w-16 h-16 bg-[#C6A54A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-8 h-8 text-[#C6A54A]" />
            </div>
            <CardTitle className="text-white text-xl">¡Has sido invitado!</CardTitle>
            <p className="text-zinc-400 mt-2">
              <span className="text-[#C6A54A] font-semibold">{account?.owner_name}</span> te ha invitado a unirte a su equipo
            </p>
          </CardHeader>
          
          <CardContent className="p-6">
            {/* Invitation Details */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <span className="text-zinc-400 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Correo
                </span>
                <span className="text-white">{invitation?.email}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <span className="text-zinc-400">Tu rol será</span>
                <Badge className={roleInfo.color}>
                  <RoleIcon className="w-3 h-3 mr-1" />
                  {roleInfo.label}
                </Badge>
              </div>
              
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-blue-400 text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {roleInfo.description}
                </p>
              </div>
            </div>

            {/* Registration Form */}
            <form onSubmit={handleAccept} className="space-y-4">
              <div>
                <Label className="text-zinc-400">Tu nombre</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="text"
                    placeholder="Tu nombre completo"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                    data-testid="accept-invite-name"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-400">Crear contraseña</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mínimo 6 caracteres"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                    data-testid="accept-invite-password"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-400">Confirmar contraseña</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Repite la contraseña"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                    data-testid="accept-invite-confirm-password"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                  className="rounded border-zinc-700"
                />
                Mostrar contraseñas
              </label>

              <Button
                type="submit"
                disabled={accepting}
                className="w-full h-11 bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-bold"
                data-testid="accept-invite-btn"
              >
                {accepting ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Aceptar Invitación
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-zinc-500 text-xs mt-6">
          Al aceptar, podrás acceder al sistema con las credenciales que crees.
        </p>
      </div>
    </div>
  );
}
